import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from './ui/Card'
import { MapaEstados } from './MapaEstados'

type Analytics = {
  total: number
  porDia: { dia: string; pedidos: number }[]
  produtos: { sku: string | null; nome: string | null; unidades: number; pedidos: number }[]
  estados: { uf: string; pedidos: number }[]
  semUf: number
  transportadoras: { transportadora: string; pedidos: number }[]
  canais: { canal: string; pedidos: number }[]
}

function Barra({ label, valor, max, sufixo }: { label: string; valor: number; max: number; sufixo?: string }) {
  const pct = max > 0 ? Math.round((valor / max) * 100) : 0
  return (
    <div className="flex items-center gap-3">
      <span className="w-40 shrink-0 truncate text-sm text-gray-700" title={label}>{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
        <div className="h-full rounded-full bg-blue-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-20 shrink-0 text-right text-sm font-semibold text-gray-900">
        {valor.toLocaleString('pt-BR')}{sufixo ?? ''}
      </span>
    </div>
  )
}

/** As escalas herdam o período e o canal do filtro único da tela — não têm
 *  filtro próprio, para não existirem dois recortes na mesma página. */
export function DashboardEscalas({ tenantId, from, to, canal }: {
  tenantId: string; from: string; to: string; canal: string
}) {
  const { data } = useQuery<Analytics>({
    queryKey: ['dashboard-analytics', tenantId, from, to, canal],
    queryFn: async () => (await api.get('/outbound/dashboard/analytics', {
      params: { tenantId, from, to, ...(canal ? { channel: canal } : {}) },
    })).data,
    enabled: !!tenantId,
  })

  const maxProd = Math.max(1, ...(data?.produtos ?? []).map(p => p.unidades))
  const maxTransp = Math.max(1, ...(data?.transportadoras ?? []).map(t => t.pedidos))
  const maxUf = Math.max(1, ...(data?.estados ?? []).map(e => e.pedidos))
  const maxDia = Math.max(1, ...(data?.porDia ?? []).map(d => d.pedidos))

  return (
    <div className="space-y-5">
      {/* ── Por dia ── */}
      <Card>
        <CardHeader><span className="text-sm font-semibold text-gray-900">Pedidos por dia</span></CardHeader>
        <CardContent>
          {!data?.porDia.length ? (
            <p className="py-6 text-center text-sm text-gray-400">Nada no período.</p>
          ) : (
            <div className="flex items-end gap-1 overflow-x-auto pb-1">
              {data.porDia.map(d => (
                <div key={d.dia} className="flex min-w-[20px] flex-1 flex-col items-center gap-1" title={`${d.dia}: ${d.pedidos} pedido(s)`}>
                  <span className="text-[10px] tabular-nums text-gray-400">{d.pedidos}</span>
                  {/* Altura em PIXEL, não em %: a coluna tem altura automática, e
                      porcentagem sem altura definida no pai resolve para zero —
                      era o que deixava as barras achatadas num traço. */}
                  <div className="w-full rounded-t bg-blue-500"
                    style={{ height: Math.max(3, Math.round((d.pedidos / maxDia) * 132)) }} />
                  <span className="text-[9px] text-gray-400">{d.dia.slice(8)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* ── Mapa por estado ── */}
        <Card>
          <CardHeader>
            <span className="text-sm font-semibold text-gray-900">Envios por estado</span>
            {!!data?.semUf && (
              <span className="text-xs text-amber-600">{data.semUf} sem UF no cadastro</span>
            )}
          </CardHeader>
          <CardContent>
            <MapaEstados estados={data?.estados ?? []} />
          </CardContent>
        </Card>

        {/* ── Escala de estados ── */}
        <Card>
          <CardHeader><span className="text-sm font-semibold text-gray-900">Estados</span></CardHeader>
          <CardContent className="space-y-2">
            {!data?.estados.length ? (
              <p className="py-6 text-center text-sm text-gray-400">Sem endereço no período.</p>
            ) : data.estados.slice(0, 12).map(e => (
              <Barra key={e.uf} label={e.uf} valor={e.pedidos} max={maxUf} />
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* ── Escala de produtos ── */}
        <Card>
          <CardHeader>
            <span className="text-sm font-semibold text-gray-900">Produtos vendidos</span>
            <span className="text-xs text-gray-400">por unidades</span>
          </CardHeader>
          <CardContent className="space-y-2">
            {!data?.produtos.length ? (
              <p className="py-6 text-center text-sm text-gray-400">Nada no período.</p>
            ) : data.produtos.slice(0, 15).map(p => (
              <Barra key={p.sku ?? p.nome ?? '?'}
                label={`${p.sku ?? '—'} · ${p.nome ?? 'sem nome'}`}
                valor={p.unidades} max={maxProd} sufixo=" un" />
            ))}
          </CardContent>
        </Card>

        {/* ── Escala de transportadoras ── */}
        <Card>
          <CardHeader><span className="text-sm font-semibold text-gray-900">Transportadoras</span></CardHeader>
          <CardContent className="space-y-2">
            {!data?.transportadoras.length ? (
              <p className="py-6 text-center text-sm text-gray-400">Nada no período.</p>
            ) : data.transportadoras.slice(0, 12).map(t => (
              <Barra key={t.transportadora} label={t.transportadora} valor={t.pedidos} max={maxTransp} />
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
